/* iceberg-ui */
import { Box, Card, CardBody, Flex } from '@auviknetworks/iceberg-ui';
import { CircularProgress, List, ListItem, Typography } from '@mui/material';

/* Shared */
import {
    AppNameLinkCell,
    ApplicationBusinessOwnerCell,
    SelectAllHeaderCell,
    SelectRowCell
} from 'application/Shared/Components/Cells';
import {
    Header,
    ManagedTableV2ContextProvider,
    ManagedTableV2UI,
    useManagedTableV2Context
} from 'application/Shared/Components/Table';
import { ColumnDef } from 'application/Shared/Components/Table/types';
import { FontSizes, TABLENAMES } from 'application/Shared/Constants';
import { Payload } from 'application/Shared/Helpers';
import { TenantApp, TenantAppParams } from 'application/Shared/types/tenantApp';
import { UserApp } from 'application/Shared/types/userApp';

/* Hooks */
import { useFetchTenantApplicationLink } from 'hooks/dataAccess/Tenant/ApplicationLink/tenantApplicationLink';

/* React */
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

import { getWorkatoStatus } from '../functions';
import { WorkatoActions, WorkatoStatus } from '../types';
import { useConnectionsAndRecipesContext } from './ConnectionsAndRecipesContext';
import { OffboardSelectUsers } from './OffboardSelectUsers';
import { OnboardUserDetails } from './OnboardUserDetails';
import { FORM_BOTTOM_MARGIN } from './constants';
import { RecipeWithTenantApp } from './types';

const OnboardIntro = () => {
    const { t } = useTranslation();
    return (
        <>
            <Typography variant="body2" marginBottom="16px">
                {t('SaasOps.onboard.intro.partOne')}
            </Typography>
            <List>
                <ListItem disablePadding>
                    <Typography
                        variant="body2"
                        dangerouslySetInnerHTML={{
                            __html: t('SaasOps.onboard.intro.partTwo')
                        }}
                    />{' '}
                </ListItem>
                <ListItem disablePadding>
                    <Typography
                        variant="body2"
                        dangerouslySetInnerHTML={{
                            __html: t('SaasOps.onboard.intro.partThree')
                        }}
                    />{' '}
                </ListItem>
            </List>
        </>
    );
};

const OffboardIntro = () => {
    const { t } = useTranslation();
    return (
        <Typography variant="body2">
            {t('SaasOps.offboard.selectUsersAndApps')}
        </Typography>
    );
};

const Intro = ({ action }: { action: WorkatoActions }) => (
    <Box sx={{ margin: '16px' }}>
        {action === WorkatoActions.onboard ? (
            <OnboardIntro />
        ) : (
            <OffboardIntro />
        )}
    </Box>
);

const UserDetails = ({ action }: { action: WorkatoActions }) =>
    action === WorkatoActions.onboard ? (
        <OnboardUserDetails />
    ) : (
        <OffboardSelectUsers />
    );

const NumberOfAppsSelected = () => {
    const { t } = useTranslation();
    const { table } = useManagedTableV2Context();

    const selectedCount = table?.getSelectedRowModel().rows.length;
    const rowCount = table?.getPreFilteredRowModel().rows.length;

    return (
        <Box marginBottom="16px" marginTop="18px">
            <Typography
                variant="body2"
                color="textSecondary"
                fontWeight={600}
                ml="16px"
                style={{
                    fontSize: FontSizes[2]
                }}
            >
                {t('SaasOps.appSelection.numberOfAppsSelected', {
                    count: selectedCount,
                    rowCount
                })}
            </Typography>
        </Box>
    );
};

const recipesTableColumns: ColumnDef<UserApp, string>[] = [
    {
        accessorKey: 'checkBox',
        cell: SelectRowCell,
        enableHiding: false,
        enableSorting: false,
        header: SelectAllHeaderCell,
        id: 'selectAll',
        maxSize: 50,
        meta: {
            align: 'center'
        },
        size: 50,
        testId: 'selectAll'
    },
    {
        accessorKey: 'application.name',
        cell: (props) => (
            <AppNameLinkCell
                nameHasLink={false}
                withCategory={false}
                {...props}
            />
        ),
        header: Header,
        id: 'application',
        name: 'Common.application',
        size: 400,
        testId: 'application'
    },
    {
        accessorKey: 'tenantApp',
        cell: ApplicationBusinessOwnerCell,
        enableSorting: false,
        header: Header,
        id: 'businessOwnerType',
        name: 'Common.business_owner',
        size: 400,
        testId: 'businessOwner'
    }
];

/**
 * Component that displays a table for selecting users and applications for SaaS operations
 * Handles both onboarding and offboarding workflows
 * Shows a list of applications with Workato recipes that have connected providers
 * Includes user selection, application selection table, and relevant intro text based on action type
 *
 * @component
 * @param {Object} props - Component props
 * @param {WorkatoActions} props.action - The type of action being performed (onboard/offboard)
 * @returns {JSX.Element} SaaS operations user and application selection interface
 */
export const SaasOpsUserAndAppSelection = ({
    action
}: {
    action: WorkatoActions;
}) => {
    const { t } = useTranslation();
    const { clientId } = useParams();

    const [loading, setLoading] = useState<boolean>(true);
    const [recipesWithTenantApp, setRecipesWithTenantApp] = useState<
        RecipeWithTenantApp[]
    >([]);

    const { recipes, connectionsByProvider } =
        useConnectionsAndRecipesContext();

    // Only show recipes with a connected connection
    const filteredRecipes = useMemo(
        () =>
            recipes.filter((recipe) => {
                const workatoProvider = recipe?.application?.workatoProvider;
                if (!workatoProvider) {
                    return false;
                }

                const connection = connectionsByProvider[workatoProvider];

                return getWorkatoStatus(connection) === WorkatoStatus.connected;
            }),
        [recipes, connectionsByProvider]
    );

    // load tenant apps and merge them into each connection for the business owner section.
    const tenantAppParams = useMemo(() => {
        const baseParams: TenantAppParams = {
            includeApp: true,
            includeWorkato: true,
            onlyAppsWithWorkatoProvider: true,
            size: -1
        };

        if (filteredRecipes?.length) {
            const parsedApplicationIds = Payload.idString(
                filteredRecipes.map((recipe) => recipe.applicationId)
            );
            baseParams.applicationId = parsedApplicationIds;
        }

        return baseParams;
        // We really only care about performing this check on initial load, which is why the deps array is empty.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const { tenantApps, loading: tenantAppLoading } =
        useFetchTenantApplicationLink({
            clientId,
            params: tenantAppParams
        });

    useEffect(() => {
        if (tenantAppLoading) {
            return;
        }

        const tenantAppsByAppId = Payload.responseMap(
            tenantApps,
            'applicationId'
        ) as Record<string, TenantApp>;

        const recipesWithTenantAppLocal = filteredRecipes.map((recipe) => {
            const tenantApp = tenantAppsByAppId[recipe.applicationId!] || {};

            return {
                ...recipe,
                tenantApp
            };
        });
        setRecipesWithTenantApp(recipesWithTenantAppLocal);
        setLoading(false);
    }, [filteredRecipes, tenantAppLoading, tenantApps]);

    if (loading) {
        return (
            <Flex justifyContent="center" mt={5}>
                <CircularProgress />
            </Flex>
        );
    }

    const tableName = `${TABLENAMES.SAAS_OPS_RECIPES}-${action}`;

    return (
        <ManagedTableV2ContextProvider
            columns={recipesTableColumns}
            enableColumnFilters={false}
            getRowId="applicationId"
            ignoreSearchParams
            mode="client"
            rows={recipesWithTenantApp}
            rowsPerPage={10}
            searchEnabled
            sortDirection="ASC"
            sortKey="application"
            tableName={tableName}
        >
            <Card
                p="16px"
                margin="16px"
                marginBottom={FORM_BOTTOM_MARGIN}
                position="relative"
            >
                <Intro action={action} />

                <CardBody>
                    <UserDetails action={action} />

                    <Card
                        mt="24px"
                        variant="outlined"
                        data-testid="onboardUserAppTable"
                    >
                        <Box sx={{ margin: '16px' }}>
                            <Typography
                                variant="h6"
                                color="textSecondary"
                                fontWeight={600}
                            >
                                {t('Common.applications')}
                            </Typography>
                            <hr />
                        </Box>

                        <NumberOfAppsSelected />
                        <ManagedTableV2UI renderFooter />
                    </Card>
                </CardBody>
            </Card>
        </ManagedTableV2ContextProvider>
    );
};
